/**
 * What the boxes say before you type anything.
 *
 * One rule, applied field by field:
 *
 *  1. What the plan prescribes for that field. The plan is the reason you are
 *     standing there — 3 × 10 should not need typing, and it does not stop
 *     being the target just because last week went differently.
 *     THE ONE EXCEPTION IS REPS MID-EXERCISE: once a working set of this
 *     exercise is logged today, the next set's reps start from that set, not
 *     the plan. If set one of a 3 × 10 stopped at 8, set two is realistically
 *     another 8, and retyping it after every set was the complaint that
 *     changed this (2026-09-15). A warm-up does not count — its reps say
 *     nothing about the working sets — and last week's history still loses to
 *     the plan.
 *  2. The last set you actually did: this session's previous set if the
 *     exercise is already under way, otherwise the final working set of the
 *     last time you did it. Carried across verbatim.
 *  3. Empty. A first-ever exercise with no plan gets blank boxes rather than a
 *     guess, because there is nothing to base one on.
 *
 * The fallback is per *field*, not per source, and that matters: plans are
 * almost always silent about load — `target_weight_kg` is null in every plan
 * written so far, because nothing offers a way to set one. If a rep
 * prescription blanked the weight box, every set of every planned workout
 * would need the same number typed in again.
 *
 * Weights the app chooses are snapped to something a gym can load, in the unit
 * being shown. A weight somebody typed is carried forward exactly instead: it
 * is theirs, it is what happened, and re-rounding it would quietly edit a
 * record.
 *
 * What is deliberately *not* here any more is the progression suggestion.
 * `suggestNext` used to fill these boxes — last session's weight plus an
 * increment when you cleared the rep range. It still writes the coaching line
 * above the form ("hit 10 on every set — add weight and reset to..."), but the
 * boxes now show what the plan asked for, or what you last did, and adding the
 * increment is a decision the user makes rather than one the app makes on
 * their behalf.
 *
 * The prescription also belongs *inside* the progression rule, not layered on
 * top of it. `suggestNext` takes repsLow/repsHigh and nothing ever passed them,
 * so double progression ran against a hardcoded 5-8 band: on a plan
 * prescribing sets of 10 it announced "hit 8 on every set — add weight and
 * reset to 5", advice that contradicts the prescription printed directly above
 * it. `repsBand` is what feeds it, and it is still exported for that.
 *
 * Dexie-free, so all of this is reachable from a test.
 */

import { durationToInput, metresToDisplay, kgToDisplay, roundToGymStep,
         type DistanceUnit, type Unit } from './units.ts'

/** The prescription fields this file reads. */
export type Prescribed = {
  target_reps_low: number
  target_reps_high: number
  target_duration_seconds: number | null
  target_distance_m: number | null
  /** The load the plan asks for, if it asks for one. Null in every plan written
   *  so far, because nothing has ever offered a way to set it. */
  target_weight_kg?: number | null
}

/** The measures of an already-logged set. */
export type LoggedSet = {
  weight_kg?: number | null
  reps?: number | null
  duration_seconds?: number | null
  distance_m?: number | null
  /** A warm-up's reps never stand in for the plan's. */
  is_warmup?: boolean | null
}

export type Prefill = {
  weight: string
  reps: string
  duration: string
  distance: string
  /** The same two numbers unrounded, for callers that need kg rather than the
   *  display string — the voice logger fills a spoken "ten reps" from these,
   *  and it should agree with what the boxes show. */
  weightKg: number | null
  repsCount: number | null
}

/**
 * The rep band to run double progression against, or null to keep the default.
 *
 * Zero is not a band. An exercise measured in seconds or metres carries 0/0, and
 * feeding that in would set repsHigh to 0 — every set then "clears the range",
 * so the rule would recommend adding weight after every session forever. That is
 * the bug this guard exists for, not a hypothetical.
 */
export function repsBand(
  item: Prescribed | null | undefined,
): { repsLow: number; repsHigh: number } | null {
  if (!item) return null
  const low = item.target_reps_low || item.target_reps_high
  const high = item.target_reps_high || item.target_reps_low
  if (!low || !high) return null
  // Tolerate a reversed band rather than trusting the order.
  return { repsLow: Math.min(low, high), repsHigh: Math.max(low, high) }
}

export function prefillFrom(opts: {
  /** The last set logged for this exercise in this session, if any. */
  lastSet?: LoggedSet | null
  /** The last working set of the previous session's performance, if there is one. */
  history?: LoggedSet | null
  /** The plan item for this exercise, if the workout came from a plan. */
  prescription?: Prescribed | null
  unit: Unit
  /** The exercise's own loading step, for snapping kg targets. */
  incrementKg?: number | null
  /** The distance unit in force, so the box is prefilled in the unit its
   *  label claims. */
  du?: DistanceUnit
}): Prefill {
  const { lastSet, history, prescription, unit, incrementKg, du = 'km' } = opts

  // The most recent set they actually did. Mid-exercise that is the set before
  // this one; on the first set of the day it is how the exercise finished last
  // time. Nothing distinguishes the two here — both are "what you did", and
  // both lose to a prescription and beat a blank box.
  const carried = lastSet ?? history ?? null
  const band = repsBand(prescription)

  // A weight the app chose gets snapped to something loadable; a weight a
  // person logged is reproduced exactly. 227.5 lb is not a number this app
  // would have picked, but if that is what went on the bar then that is what
  // the next set starts from.
  const prescribedKg = prescription?.target_weight_kg
    ? roundToGymStep(prescription.target_weight_kg, unit, incrementKg) || null
    : null

  const weightKg = prescribedKg ?? carried?.weight_kg ?? null
  // Reps you just did today beat the plan — see the exception in the header.
  // `||` rather than `??`: a working set logged with no rep count has nothing
  // to repeat, so the plan still fills the box.
  const repsToday = lastSet && !lastSet.is_warmup ? lastSet.reps || null : null
  // A range only survives in plans written before prescriptions became a single
  // number; its low end is the set you are being asked to start with.
  const reps = repsToday ?? band?.repsLow ?? carried?.reps ?? null
  const durationSeconds =
    prescription?.target_duration_seconds || carried?.duration_seconds || null
  const distanceM = prescription?.target_distance_m || carried?.distance_m || null

  return {
    weight: str(kgToDisplay(weightKg, unit)),
    reps: str(reps),
    duration: durationToInput(durationSeconds),
    distance: str(metresToDisplay(distanceM, du)),
    weightKg,
    repsCount: reps,
  }
}

function str(v: number | null | undefined): string {
  return v === null || v === undefined ? '' : String(v)
}
